import React from 'react';
import { Avatar, Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { sbg } from '../../theme/sbgTokens';
import { initialsOf } from '../../utils/format';

/* ------------------------------------------------------------------------ */
/* IconTile (book §9.2)                                                      */
/* ------------------------------------------------------------------------ */

export const IconTile: React.FC<{
  children: React.ReactNode;
  /** default: Blue 50 tile · navy: one featured item · volt: a money moment (rare in EMS). */
  tone?: 'default' | 'navy' | 'volt';
  size?: 32 | 40;
  sx?: SxProps<Theme>;
}> = ({ children, tone = 'default', size = 32, sx }) => (
  <Box
    aria-hidden="true"
    sx={{
      width: size,
      height: size,
      flex: 'none',
      display: 'grid',
      placeItems: 'center',
      borderRadius: size === 40 ? sbg.radius.lg : sbg.radius.md,
      ...(tone === 'navy'
        ? { bgcolor: sbg.palette.blue[800], color: sbg.palette.slate[0] }
        : tone === 'volt'
          ? { bgcolor: sbg.palette.volt[300], color: sbg.palette.blue[800] }
          : { bgcolor: sbg.bg.tile, color: sbg.icon.tile }),
      '& > svg': { width: size === 40 ? 20 : 16, height: size === 40 ? 20 : 16 },
      ...sx,
    }}
  >
    {children}
  </Box>
);

/* ------------------------------------------------------------------------ */
/* PersonAvatar & PersonCell (book §9.5, §11.16)                             */
/* ------------------------------------------------------------------------ */

const AVATAR_FONT: Record<number, number> = { 20: 9, 24: 10, 32: 13, 40: 15, 56: 20 };

/** Circle avatar: a photo when there is one, else initials on Blue 50. */
export const PersonAvatar: React.FC<{ name?: string | null; src?: string | null; size?: 20 | 24 | 32 | 40 | 56; sx?: SxProps<Theme> }> = ({
  name,
  src,
  size = 32,
  sx,
}) => (
  <Avatar
    src={src ?? undefined}
    alt={src ? name ?? '' : undefined}
    sx={{
      width: size,
      height: size,
      flex: 'none',
      bgcolor: sbg.bg.avatar,
      color: sbg.text.avatar,
      font: `600 ${AVATAR_FONT[size]}px/1 ${sbg.font.body}`,
      ...sx,
    }}
  >
    {initialsOf(name)}
  </Avatar>
);

/** A person in a table cell or list: avatar, name and an optional secondary line. */
export const PersonCell: React.FC<{
  name: React.ReactNode;
  secondary?: React.ReactNode;
  avatarName?: string | null;
  src?: string | null;
  size?: 24 | 32 | 40;
  sx?: SxProps<Theme>;
}> = ({ name, secondary, avatarName, src, size = 32, sx }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, ...sx }}>
    <PersonAvatar name={avatarName ?? (typeof name === 'string' ? name : null)} src={src} size={size} />
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="labelMd" component="div" noWrap sx={{ color: sbg.text.primary }}>
        {name}
      </Typography>
      {secondary && (
        <Typography variant="body2" component="div" noWrap sx={{ color: sbg.text.secondary }}>
          {secondary}
        </Typography>
      )}
    </Box>
  </Box>
);

/* ------------------------------------------------------------------------ */
/* SpecGrid (book §11.9): hairlines and big numbers, no boxes                */
/* ------------------------------------------------------------------------ */

export interface SpecItem {
  label: React.ReactNode;
  value: React.ReactNode;
  caption?: React.ReactNode;
}

export const SpecGrid: React.FC<{ items: SpecItem[]; minColumnWidth?: number; size?: 'md' | 'lg'; sx?: SxProps<Theme> }> = ({
  items,
  minColumnWidth = 160,
  size = 'md',
  sx,
}) => (
  <Box
    component="dl"
    sx={{
      m: 0,
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fit, minmax(${minColumnWidth}px, 1fr))`,
      columnGap: 4,
      rowGap: 3,
      ...sx,
    }}
  >
    {items.map((item, index) => (
      <Box key={index} sx={{ borderTop: `1px solid ${sbg.border.default}`, pt: 2, display: 'grid', gap: 0.75, alignContent: 'start', minWidth: 0 }}>
        <Typography component="dt" variant="overline" sx={{ color: sbg.text.accent }}>
          {item.label}
        </Typography>
        <Box component="dd" sx={{ m: 0, display: 'grid', gap: 0.75 }}>
          <Typography component="span" variant={size === 'lg' ? 'numericLg' : 'numericMd'} sx={{ color: sbg.text.primary, overflowWrap: 'anywhere' }}>
            {item.value}
          </Typography>
          {item.caption && (
            <Typography component="span" variant="body2" sx={{ color: sbg.text.secondary }}>
              {item.caption}
            </Typography>
          )}
        </Box>
      </Box>
    ))}
  </Box>
);

/* ------------------------------------------------------------------------ */
/* DefinitionList: label/value rows for record details                       */
/* ------------------------------------------------------------------------ */

export const DefinitionList: React.FC<{ items: { label: React.ReactNode; value: React.ReactNode }[]; columns?: 1 | 2; sx?: SxProps<Theme> }> = ({
  items,
  columns = 1,
  sx,
}) => (
  <Box
    component="dl"
    sx={{
      m: 0,
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: columns === 2 ? 'repeat(2, minmax(0, 1fr))' : '1fr' },
      columnGap: 4,
      ...sx,
    }}
  >
    {items.map((item, index) => (
      <Box
        key={index}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(120px, 40%) 1fr',
          gap: 2,
          py: 1.25,
          borderBottom: `1px solid ${sbg.border.subtle}`,
          minWidth: 0,
        }}
      >
        <Typography component="dt" variant="body1" sx={{ color: sbg.text.secondary }}>
          {item.label}
        </Typography>
        <Typography component="dd" variant="body1" sx={{ m: 0, color: sbg.text.primary, minWidth: 0, overflowWrap: 'anywhere' }}>
          {item.value}
        </Typography>
      </Box>
    ))}
  </Box>
);

/* ------------------------------------------------------------------------ */
/* StageRail (book §11.12): the mark as a component — one per view           */
/* ------------------------------------------------------------------------ */

export type StageState = 'done' | 'current' | 'upcoming' | 'stopped' | 'projected';

export const StageRail: React.FC<{ stages: { label: string; state: StageState }[]; 'aria-label'?: string; sx?: SxProps<Theme> }> = ({
  stages,
  sx,
  ...rest
}) => {
  const currentIndex = stages.findIndex((stage) => stage.state === 'current' || stage.state === 'stopped');
  return (
    <Box
      component="ol"
      aria-label={rest['aria-label'] ?? 'Progress'}
      sx={{ listStyle: 'none', m: 0, p: '0 4px', display: 'grid', gridAutoFlow: 'column', gridAutoColumns: '1fr', gap: 1, ...sx }}
    >
      {stages.map((stage, index) => (
        <Box
          component="li"
          key={stage.label}
          aria-current={index === currentIndex ? 'step' : undefined}
          sx={{
            display: 'grid',
            gap: 1,
            minWidth: 0,
            '&::before': {
              content: '""',
              display: 'block',
              height: 8,
              borderRadius: '2px',
              transform: 'skewX(-30deg)',
              background:
                stage.state === 'done' || stage.state === 'current'
                  ? sbg.action.primary
                  : stage.state === 'stopped'
                    ? sbg.palette.danger[500]
                    : stage.state === 'projected'
                      ? `repeating-linear-gradient(-30deg, ${sbg.bg.track} 0 3px, ${sbg.border.default} 3px 5px)`
                      : sbg.bg.track,
            },
          }}
        >
          {/* Inside the item, so the list holds only items: "Step 2 of 4: In review, current step". */}
          {index === currentIndex && <span className="sbg-sr-only">{`Step ${index + 1} of ${stages.length}: `}</span>}
          <Typography
            variant="caption"
            component="span"
            noWrap
            sx={{ color: index === currentIndex ? sbg.text.primary : sbg.text.secondary }}
          >
            {stage.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/* ------------------------------------------------------------------------ */
/* Meter: a labelled 4px progress bar (utilization, usage against allotment)  */
/* ------------------------------------------------------------------------ */

export const Meter: React.FC<{
  value: number;
  max?: number;
  /** Status color only when the state is true (over allotment = danger). */
  tone?: 'brand' | 'success' | 'warning' | 'danger' | 'neutral';
  label?: React.ReactNode;
  valueLabel?: React.ReactNode;
  height?: 4 | 6 | 8;
  'aria-label'?: string;
  sx?: SxProps<Theme>;
}> = ({ value, max = 100, tone = 'brand', label, valueLabel, height = 4, sx, ...rest }) => {
  const labelId = React.useId();
  // A meter reports a value inside a positive range. A zero allotment or a
  // rate past 100% still draws (empty, or full) and still reads as valid.
  const hasRange = Number.isFinite(max) && max > 0;
  const ceiling = hasRange ? max : 1;
  const now = hasRange && Number.isFinite(value) ? Math.max(0, Math.min(ceiling, value)) : 0;
  const pct = (now / ceiling) * 100;
  const labelledBy = !rest['aria-label'] && label ? labelId : undefined;
  const fill =
    tone === 'success' ? sbg.status.success.icon
      : tone === 'warning' ? sbg.palette.warning[500]
        : tone === 'danger' ? sbg.status.danger.icon
          : tone === 'neutral' ? sbg.palette.slate[400]
            : sbg.action.primary;
  return (
    <Box sx={{ display: 'grid', gap: 0.75, minWidth: 0, ...sx }}>
      {(label || valueLabel) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          {label && <Typography id={labelledBy} variant="body2" component="span" sx={{ color: sbg.text.secondary }}>{label}</Typography>}
          {valueLabel && <Typography variant="labelSm" component="span" sx={{ color: sbg.text.primary, fontVariantNumeric: 'tabular-nums' }}>{valueLabel}</Typography>}
        </Box>
      )}
      {/* A measurement against a limit (usage, a rate), not the progress of a task. */}
      <Box
        role="meter"
        aria-valuemin={0}
        aria-valuemax={ceiling}
        aria-valuenow={now}
        aria-label={rest['aria-label']}
        aria-labelledby={labelledBy}
        sx={{ height, borderRadius: sbg.radius.pill, bgcolor: sbg.bg.track, overflow: 'hidden' }}
      >
        <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: fill, borderRadius: 'inherit', transition: `width ${sbg.duration.slow} ${sbg.ease.enter}` }} />
      </Box>
    </Box>
  );
};
